// Theme switching for the RezRATE design system (REZ-1299).
// The user picks a preference: 'light', 'dark' or 'system', stored in localStorage as
// `rr-theme`. 'system' follows prefers-color-scheme and updates live.
// Dark mode is opt-in while it is in testing: with nothing stored, the default is 'light'.
// To make 'system' the default once dark mode is proven, change DEFAULT_PREFERENCE and the
// matching fallback in public/index.html.
// public/index.html runs the same resolution inline before first paint, so keep them in sync.

const STORAGE_KEY = 'rr-theme';
export const THEME_PREFERENCES = ['system', 'light', 'dark'];
export const DEFAULT_PREFERENCE = 'light';

const darkQuery = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;
const listeners = new Set();

const readStored = () => {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch (e) {
    return null;
  }
};

const writeStored = (preference) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, preference);
  } catch (e) {
    // Storage blocked (private mode); the theme still applies for this page load.
  }
};

export const getThemePreference = () => {
  const stored = readStored();
  return THEME_PREFERENCES.includes(stored) ? stored : DEFAULT_PREFERENCE;
};

const resolve = (preference) => {
  if (preference === 'light' || preference === 'dark') return preference;
  return darkQuery && darkQuery.matches ? 'dark' : 'light';
};

const apply = (theme) => {
  const root = document.documentElement;
  root.setAttribute('data-theme', theme);
  // Bootstrap 5.3 reads data-bs-theme for its own dark styles.
  if (theme === 'dark') {
    root.setAttribute('data-bs-theme', 'dark');
  } else {
    root.removeAttribute('data-bs-theme');
  }
};

const notify = () => {
  const state = { preference: getThemePreference(), theme: getTheme() };
  listeners.forEach((listener) => listener(state));
};

/** The theme currently applied: 'light' or 'dark'. */
export const getTheme = () => (document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light');

export const setThemePreference = (preference) => {
  const next = THEME_PREFERENCES.includes(preference) ? preference : DEFAULT_PREFERENCE;
  writeStored(next);
  apply(resolve(next));
  notify();
  return next;
};

/** Calls `listener({ preference, theme })` on every change; returns an unsubscribe function. */
export const subscribeTheme = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const initTheme = () => {
  apply(resolve(getThemePreference()));
  if (darkQuery) {
    const onSystemChange = () => {
      if (getThemePreference() === 'system') {
        apply(resolve('system'));
        notify();
      }
    };
    if (darkQuery.addEventListener) {
      darkQuery.addEventListener('change', onSystemChange);
    } else if (darkQuery.addListener) {
      darkQuery.addListener(onSystemChange);
    }
  }
  return getTheme();
};
